export const getJoiningFee = (plan) => {
  const charges = plan?.simulated_contract?.charges;
  const joiningCharges = Array.isArray(charges)
    ? charges.filter((charge) => charge && charge.type === "JoiningFee")
    : [];
  const isValidAmount = (value) =>
    (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) &&
    Number.isFinite(Number(value)) &&
    Number(value) >= 0;

  // Simulated charges include fee discounts; a waived fee must stay zero.
  if (joiningCharges.length && joiningCharges.every((charge) => isValidAmount(charge.amount))) {
    return (
      Math.round(joiningCharges.reduce((total, charge) => total + Number(charge.amount), 0) * 100) /
      100
    );
  }

  const gross = plan?.joiningFee?.gross;
  return isValidAmount(gross) ? Number(gross) : 0;
};

export const normalizePlanType = (value) => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();

  if (normalizedValue === "premium" || normalizedValue === "2") return "premium";
  if (normalizedValue === "single" || normalizedValue === "1") return "single";
  return normalizedValue ? "other" : null;
};

export const getPlanType = (plan) => {
  const attribute = plan?.customAttributes?.find(
    (item) => item.displayName?.trim().toLowerCase() === "plan type",
  );

  if (!attribute) return null;
  return normalizePlanType(attribute.displayValue || attribute.rawValue);
};

export const normalizeAttributeName = (value) => {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
};

export const getPlanVoucherCode = (plan) => {
  const attributes = Array.isArray(plan?.customAttributes) ? plan.customAttributes : [];
  const attribute = attributes.find(
    (item) => normalizeAttributeName(item?.displayName || item?.name) === "voucher_code",
  );

  if (!attribute) return "";

  const value = [attribute.displayValue, attribute.rawValue].find(
    (candidate) => candidate !== undefined && candidate !== null && String(candidate).trim(),
  );

  return value === undefined ? "" : String(value).trim();
};

export const getNextCalendarDate = (dateString) => {
  const match = String(dateString ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

  if (
    date.getUTCFullYear() !== Number(year) ||
    date.getUTCMonth() !== Number(month) - 1 ||
    date.getUTCDate() !== Number(day)
  ) {
    return null;
  }

  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

const getLocalCalendarDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const isValidPurchaseStartDate = (value) => {
  const match = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;

  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

  return (
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day)
  );
};

// fallbackDate is "today": the API rejects a startDate before today, so past dates clamp to it.
export const normalizePurchaseStartDate = (value, fallbackDate = new Date()) => {
  const fallback = fallbackDate instanceof Date ? fallbackDate : new Date(fallbackDate);
  const safeFallback = Number.isNaN(fallback.getTime()) ? new Date() : fallback;
  const today = getLocalCalendarDate(safeFallback);

  // yyyy-MM-dd strings compare correctly as plain strings.
  if (isValidPurchaseStartDate(value) && String(value) >= today) return String(value);

  return today;
};

// "Sort Order" plan attribute: lower shows first; plans without one fall back to price.
export const getPlanSortOrder = (plan) => {
  const attribute = plan?.customAttributes?.find?.(
    (item) =>
      String(item.displayName ?? "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, " ") === "sort order",
  );
  const value = String(attribute?.rawValue ?? attribute?.displayValue ?? "").trim();
  const order = value === "" ? NaN : Number(value);
  return Number.isFinite(order) ? order : Infinity;
};

export const sortPlansByAmount = (plans) => {
  return [...plans].sort((firstPlan, secondPlan) => {
    const firstOrder = getPlanSortOrder(firstPlan);
    const secondOrder = getPlanSortOrder(secondPlan);
    if (firstOrder !== secondOrder) return firstOrder < secondOrder ? -1 : 1;

    const firstAmount = Number(
      firstPlan.amountAfterDiscount ?? firstPlan.amountBeforeDiscount ?? 0,
    );
    const secondAmount = Number(
      secondPlan.amountAfterDiscount ?? secondPlan.amountBeforeDiscount ?? 0,
    );

    return firstAmount - secondAmount || Number(firstPlan.id) - Number(secondPlan.id);
  });
};

export const getLocalizedApiMessage = (response, language, fallback) => {
  const data = response?.data ?? response ?? {};
  return (
    data.localizedMessages?.[language] ||
    data.messages?.[language] ||
    (language === "ar" ? data.messageAr : data.messageEn) ||
    data.message ||
    fallback
  );
};

export const getVoucherMessage = (response, language) => {
  const data = response?.data ?? response ?? {};
  return (
    data.voucherLocalizedMessages?.[language] ||
    data.voucherMessages?.[language] ||
    (language === "ar" ? data.voucherMessageAr : data.voucherMessageEn) ||
    data.localizedMessages?.[language] ||
    data.messages?.[language] ||
    (language === "ar" ? data.messageAr : data.messageEn) ||
    data.voucherMessage ||
    data.message ||
    ""
  );
};
